import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Container,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import Slider from "react-slick";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "./testimonials.css"; // Add this import to apply custom CSS for slick-slide
import { getDocs, collection } from "firebase/firestore";
import { db } from "../../../firestore-DB-config/firebase-config";
import Comma from "../../../assets/images/comma.png";

type Testimonial = {
  name: string;
  role: string;
  heading: string;
  message: string;
  avatar: string;
};

// Custom next arrow with updated styles
const NextArrow = (props: any) => {
  const { onClick } = props;
  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        top: "50%",
        right: -15, // Move the button further to the right
        transform: "translateY(-50%)",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        border: "2px solid rgba(0, 0, 0, 0.1)",
        zIndex: 10,
        display: { xs: "none", sm: "flex" },
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 1)",
          borderColor: "#000",
        },
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      <ChevronRightIcon fontSize="medium" />
    </IconButton>
  );
};

// Custom previous arrow with updated styles
const PrevArrow = (props: any) => {
  const { onClick } = props;
  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        top: "50%",
        left: -15,
        transform: "translateY(-50%)",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        border: "2px solid rgba(0, 0, 0, 0.1)",
        zIndex: 10,
        display: { xs: "none", sm: "flex" },
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 1)",
          borderColor: "#000",
        },
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      <ChevronLeftIcon fontSize="medium" />
    </IconButton>
  );
};

const Testimonials: React.FC = () => {
  const [reviews, setReviews] = useState<Testimonial[]>([]);

  useEffect(() => {
    getDocs(collection(db, "testimonials"))
      .then((querySnapshot) => {
        const reviewsData = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name,
            role: data.role,
            heading: data.heading,
            message: data.message,
            avatar: data.avatar,
          } as Testimonial;
        });
        setReviews(reviewsData);
      })
      .catch((error) => {
        console.error("Error getting documents: ", error);
      });
  }, []);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <Container sx={{ my: { xs: 6, md: 8 }, px: { xs: 1.5, md: 0, lg: 0 }, mb:"50px" }}>
      <Box sx={{ p: 4, textAlign: "left", position: "relative" }}>
        <Typography
          variant="h5"
          fontWeight={700}
          color="rgba(1, 29, 51, 1)"
          gutterBottom
          sx={{ fontSize: { xs: "22px", sm: "28px" } }}
        >
          TESTIMONIALS
        </Typography>
        <Typography
          variant="h4"
          fontWeight={540}
          mb={2}
          color="rgba(1, 29, 51, 1)"
          sx={{ fontSize: { xs: "24px", sm: "32px" } }} // Responsive subheading
        >
          LOOK AT WHAT OUR CUSTOMERS SAY
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "#7A7A7A", marginBottom: "20px", fontSize: { xs: "14px", sm: "16px" } }}
        >
          Donec Porttitor Euismod Dignissim. Nullam A Lacinia Ipsum, Nec
          Dignissim Purus. Nulla Convallis Ipsum Molestie Nibh Malesuada.
        </Typography>

        <Slider {...settings}>
          {reviews.map((review, index) => (
            // Review Card Section using Material-UI Card
            <Card
              key={index}
              sx={{
                p: 0, // Padding is now handled by CardContent
                height: "100%",
                backgroundColor: "white",
                borderRadius: 4,
                boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                transition:
                  "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: 3,
                },
              }}
            >
              <Container>
                <CardContent>
                  <Box
                    component="img"
                    src={Comma} // Add the path to your image here
                    alt="Quotation Image"
                    sx={{
                      height: "40px", // Adjust the height based on the size you need
                      mt: 1,
                      mb: 2,
                      textAlign: "left",
                      display: "block", // Ensure it's a block element for proper alignment
                    }}
                  />

                  <Typography
                    variant="body1"
                    sx={{ mb: 1, textAlign: "left", fontWeight: 600 }}
                  >
                    {review.heading}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{ mb: 2.5, textAlign: "left" }}
                  >
                    {review.message}
                  </Typography>

                  {/* User Section */}
                  <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                    <Avatar
                      src={review.avatar}
                      alt={review.name}
                      sx={{ mr: 1.5 }}
                    />
                    <Box>
                      <Typography variant="subtitle1" sx={{ mb: -1,}}>{review.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {review.role}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Container>
            </Card>
          ))}
        </Slider>
      </Box>
    </Container>
  );
};

export default Testimonials;
